import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { companyAPI, templateAPI } from '../services/api';
import { entityConfigApi } from '../services/entityConfigApi';
import {CompanyRequest, Currency, EntityConfig} from '../types';
import { useCompany } from '../context/CompanyContext';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../hooks/useI18n';
import { useOnboardingTour } from '../hooks/useOnboardingTour';
import Layout from '../components/Layout';
import { getErrorMessage } from '../utils/errorHandler';
import { ArrowLeft, Save, Building2, AlertCircle, Upload, X, Image, Lock, HelpCircle } from 'lucide-react';

const CompanyCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const isEditMode = !!companyId;
  const { setSelectedCompany, loadCompanies, companies } = useCompany();
  const { user } = useAuth();
  const { t, isRTL } = useI18n();
  const { startTour, manualStart, isTourCompleted } = useOnboardingTour();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode); // For edit mode data loading
  const [error, setError] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [entityConfig, setEntityConfig] = useState<EntityConfig | null>(null);
  const [formData, setFormData] = useState<CompanyRequest>({
    name: '',
    email: '',
    phone: '',
    ice: '',
    cnss: '',
    rc: '',
    fi: '',
    tradLicense: '',
    capital: 0,
    vatRate: 0,
    startingOffset: 0,
    clientStartingOffset: 0,
    supplierStartingOffset: 0,
    street: '',
    city: '',
    postalCode: '',
    country: '',
    website: '',
    rib: '',
    logo: null,
    mailUsername: '',
    mailPassword: '',
    mailSubject: '',
    mailBody: '',
    lang: 'en',
    templateId: '',
    encryptionFeatureEnabled: false,
    cashThresholdEnabled: false,
    dailyCashThreshold: 0,
    monthlyCashThreshold: 0,
    deleteLogo: false,
    currency: 'MAD'
  });


  useEffect(() => {
    const fetchConfig = async () => {
      if (user?.entityId) {
        try {
          const config = await entityConfigApi.getConfigByEntityId(user.entityId);
          setEntityConfig(config);
        } catch (error) {
          console.error('Failed to fetch entity config:', error);
        }
      }
    };
    fetchConfig();
  }, [user?.entityId]);

  // Auto-start onboarding tour on first visit (create mode only)
  useEffect(() => {
    if (!isEditMode && !initialLoading) {
      startTour({
        tourId: 'company-create',
        steps: getCompanyTourSteps(),
        autoStartDelay: 1000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, initialLoading]);

  // Load company data if in edit mode
  useEffect(() => {
    const fetchCompanyForEdit = async () => {
      if (isEditMode && companyId) {
        try {
          setInitialLoading(true);
          const companyToEdit = await companyAPI.getCompanyById(companyId, true);

          setFormData({
            name: companyToEdit.name,
            email: companyToEdit.email || '',
            phone: companyToEdit.phone || '',
            ice: companyToEdit.ice || '',
            cnss: companyToEdit.cnss || '',
            rc: companyToEdit.rc || '',
            fi: companyToEdit.fi || '',
            tradLicense: companyToEdit.tradLicense || '',
            capital: companyToEdit.capital || 0,
            vatRate: companyToEdit.vatRate || 0,
            startingOffset: companyToEdit.startingOffset || 0,
            clientStartingOffset: companyToEdit.clientStartingOffset || 0,
            supplierStartingOffset: companyToEdit.supplierStartingOffset || 0,
            street: companyToEdit.street || '',
            city: companyToEdit.city || '',
            postalCode: companyToEdit.postalCode || '',
            country: companyToEdit.country || '',
            website: companyToEdit.website || '',
            rib: companyToEdit.rib || '',
            logo: null, // Logo is handled separately
            mailUsername: companyToEdit.mailUsername || '',
            mailPassword: companyToEdit.mailPassword || '',
            mailSubject: companyToEdit.mailSubject || '',
            mailBody: companyToEdit.mailBody || '',
            lang: companyToEdit.lang || 'en',
            templateId: companyToEdit.templateId || '',
            encryptionFeatureEnabled: companyToEdit.encryptionFeatureEnabled || false,
            cashThresholdEnabled: companyToEdit.cashThresholdEnabled || false,
            dailyCashThreshold: companyToEdit.dailyCashThreshold || 0,
            monthlyCashThreshold: companyToEdit.monthlyCashThreshold || 0,
            deleteLogo: false,
            currency: companyToEdit.currency || 'MAD'
          });

          // Set logo preview if exists
          if (companyToEdit.logo) {
            setLogoPreview(`data:image/png;base64,${companyToEdit.logo}`);
          }
        } catch (err) {
          console.error('Error fetching company for edit:', err);
          setError('Failed to load company details');
        } finally {
          setInitialLoading(false);
        }
      }
    };

    fetchCompanyForEdit();
  }, [isEditMode, companyId]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEditMode && companyId) {
        await companyAPI.updateCompany(companyId, formData);
        await loadCompanies();
        navigate('/companies', {
          state: {
            message: `Company "${formData.name}" updated successfully!`,
            type: 'success'
          }
        });
      } else {
        const newCompany = await companyAPI.createCompany(formData);
        await loadCompanies();
        setSelectedCompany(newCompany.id);
        navigate('/companies', {
          state: {
            message: `Company "${newCompany.name}" created successfully!`,
            type: 'success'
          }
        });
      }
    } catch (err: any) {
      setError(getErrorMessage(err));
      console.error('Error saving company:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CompanyRequest, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setLogoError(null);
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setLogoError(t('companies.logoTypeError') || 'Please select a valid image file');
        return;
      }

      // Validate file size (max 1MB)
      if (file.size > 1 * 1024 * 1024) {
        setLogoError(t('companies.logoSizeError') || 'Logo file size must be less than 1MB');
        return;
      }

      setFormData(prev => ({
        ...prev,
        logo: file,
        deleteLogo: false
      }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const removeLogo = () => {
    setFormData(prev => ({
      ...prev,
      logo: null,
      deleteLogo: true
    }));
    setLogoPreview(null);
    setLogoError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Show loading spinner while fetching company data in edit mode
  if (initialLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('common.loading') || 'Loading...'}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-4'}`}>
            <button
              onClick={() => navigate('/companies')}
              className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'} text-gray-600 hover:text-gray-900 transition-colors`}
            >
              <ArrowLeft className={`h-5 w-5 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
              <span>{t('companies.backToCompanies')}</span>
            </button>
          </div>
          <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'}`}>
            <Building2 className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">
              {isEditMode ? t('companies.editCompany') : t('companies.createNewCompany')}
            </h1>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'}`}>
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} className="p-8">
            {/* Basic Information */}
            <div className="mb-8" id="company-basic-info">
              <h2 className={`text-xl font-semibold text-gray-900 mb-4 flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'}`}>
                <Building2 className="w-5 h-5" />
                <span>{t('companies.basicInformation')}</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.companyName')} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('companies.companyNamePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.email')}
                  </label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="company@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.phone')}
                  </label>
                  <input
                    type="tel"
                    value={formData.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('companies.phonePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.website')}
                  </label>
                  <input
                    type="url"
                    value={formData.website || ''}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('companies.websitePlaceholder')}
                  />
                </div>
              </div>
            </div>

            {/* Security Settings */}
            {entityConfig?.encryptionEnabled && (
              <div className="mb-8">
                <h2 className={`text-xl font-semibold text-gray-900 mb-4 flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'}`}>
                  <Lock className="w-5 h-5" />
                  <span>{t('companies.securitySettings') || 'Security Settings'}</span>
                </h2>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="encryptionFeatureEnabled"
                        name="encryptionFeatureEnabled"
                        type="checkbox"
                        checked={formData.encryptionFeatureEnabled || false}
                        onChange={(e) => setFormData(prev => ({ ...prev, encryptionFeatureEnabled: e.target.checked }))}
                        className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                    </div>
                    <div className={`ml-3 text-sm ${isRTL ? 'mr-3 ml-0' : 'ml-3'}`}>
                      <label htmlFor="encryptionFeatureEnabled" className="font-medium text-gray-700">
                        {t('companies.enableEncryption') || 'Enable Client-Side Encryption'}
                      </label>
                      <p className="text-gray-500">
                        {t('companies.enableEncryptionDescription') || 'If enabled, sensitive client data (Name, Email, ICE, etc.) will be encrypted in the browser before being sent to the server. You will be required to set an encryption key.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Cash Threshold Settings */}
            <div className="mb-8" id="company-cash-threshold">
              <h2 className={`text-xl font-semibold text-gray-900 mb-4 flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'}`}>
                <span>{t('companies.cashThresholdSettings') || 'Cash Threshold Settings'}</span>
              </h2>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start mb-4">
                  <div className="flex items-center h-5">
                    <input
                      id="cashThresholdEnabled"
                      name="cashThresholdEnabled"
                      type="checkbox"
                      checked={formData.cashThresholdEnabled || false}
                      onChange={(e) => setFormData(prev => ({ ...prev, cashThresholdEnabled: e.target.checked }))}
                      className="focus:ring-yellow-500 h-4 w-4 text-yellow-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className={`ml-3 text-sm ${isRTL ? 'mr-3 ml-0' : 'ml-3'}`}>
                    <label htmlFor="cashThresholdEnabled" className="font-medium text-gray-700">
                      {t('companies.enableCashThreshold') || 'Enable Cash Payment Thresholds'}
                    </label>
                    <p className="text-gray-500">
                      {t('companies.enableCashThresholdDescription') || 'When enabled, the system will warn you when creating cash invoices that exceed the configured daily or monthly thresholds.'}
                    </p>
                  </div>
                </div>

                {formData.cashThresholdEnabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-yellow-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('companies.dailyCashThreshold') || 'Daily Cash Threshold'}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.dailyCashThreshold || ''}
                        onChange={(e) => handleInputChange('dailyCashThreshold', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        placeholder="10000.00"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {t('companies.dailyCashThresholdDescription') || 'Maximum cash invoice amount allowed per day.'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('companies.monthlyCashThreshold') || 'Monthly Cash Threshold'}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.monthlyCashThreshold || ''}
                        onChange={(e) => handleInputChange('monthlyCashThreshold', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        placeholder="50000.00"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {t('companies.monthlyCashThresholdDescription') || 'Maximum cash invoice amount allowed per month.'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Legal Information */}
            <div className="mb-8" id="company-legal-info">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('companies.legalInformation')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.ice')} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.ice || ''}
                    onChange={(e) => handleInputChange('ice', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('companies.icePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.cnss')} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.cnss || ''}
                    onChange={(e) => handleInputChange('cnss', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('companies.cnssPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.rc')} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.rc || ''}
                    onChange={(e) => handleInputChange('rc', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('companies.rcPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.fi')} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.fi || ''}
                    onChange={(e) => handleInputChange('fi', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('companies.fiPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.tradLicense')}
                  </label>
                  <input
                    type="text"
                    value={formData.tradLicense || ''}
                    onChange={(e) => handleInputChange('tradLicense', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('companies.tradLicensePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.capital')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.capital || ''}
                    onChange={(e) => handleInputChange('capital', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.vatRate')} (%) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    required
                    value={formData.vatRate || ''}
                    onChange={(e) => handleInputChange('vatRate', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="20.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.startingOffset')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.startingOffset || ''}
                    onChange={(e) => handleInputChange('startingOffset', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {t('companies.startingOffsetDescription')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.clientStartingOffset')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.clientStartingOffset || ''}
                    onChange={(e) => handleInputChange('clientStartingOffset', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {t('companies.clientStartingOffsetDescription')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.supplierStartingOffset')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.supplierStartingOffset || ''}
                    onChange={(e) => handleInputChange('supplierStartingOffset', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {t('companies.supplierStartingOffsetDescription')}
                  </p>
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="mb-8" id="company-address-info">
              <h2 className='text-xl font-semibold text-gray-900 mb-4'>{t('companies.addressInformation')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.street')}
                  </label>
                  <input
                    type="text"
                    value={formData.street || ''}
                    onChange={(e) => handleInputChange('street', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('companies.streetPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.city')} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.city || ''}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('companies.cityPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.postalCode')}
                  </label>
                  <input
                    type="text"
                    value={formData.postalCode || ''}
                    onChange={(e) => handleInputChange('postalCode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('companies.postalCodePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.country')} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.country || ''}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('companies.countryPlaceholder')}
                  />
                </div>
              </div>
            </div>

            {/* Logo Upload */}
            <div className="mb-8" id="company-logo-upload">
              <h2 className='text-xl font-semibold text-gray-900 mb-4'>{t('companies.companyLogo')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.uploadLogo')}
                  </label>
                  <div className="space-y-4">
                    {logoPreview ? (
                      <div className="relative">
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="w-32 h-32 object-contain border border-gray-300 rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={removeLogo}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                      >
                        <Upload className="w-8 h-8 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500 text-center">{t('companies.clickToUpload')}</p>
                        <p className="text-xs text-gray-400 text-center">{t('companies.supportedFormats')}</p>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <p className="text-xs text-gray-500">
                      {t('companies.logoRecommendations')}
                    </p>
                    {logoError && (
                      <p className="text-xs text-red-500 mt-1 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {logoError}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice Settings */}
            <div className="mb-8" id="company-invoice-settings">
              <h2 className='text-xl font-semibold text-gray-900 mb-4'>{t('companies.invoiceSettings')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">


                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.rib')}
                  </label>
                  <input
                    type="text"
                    value={formData.rib || ''}
                    onChange={(e) => handleInputChange('rib', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('companies.ribPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.currency')}
                  </label>
                  <select
                    value={formData.currency || 'MAD'}
                    onChange={(e) => handleInputChange('currency', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {Object.values(Currency).map((currency) => (
                        <option key={currency} value={currency}>
                          {currency} – {t('companies.currency'+currency.toUpperCase())}
                        </option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('companies.currencyDescription')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.language')}
                  </label>
                  <select
                    value={formData.lang || 'en'}
                    onChange={(e) => handleInputChange('lang', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="en">{t('companies.english')}</option>
                    <option value="fr">{t('companies.french')}</option>
                    <option value="ar">{t('companies.arabic')}</option>
                  </select>
                </div>
              </div>
            </div>


            {/* Email Settings
            <div className="mb-8">
              <h2 className='text-xl font-semibold text-gray-900 mb-4'>{t('companies.emailSettings')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.mailUsername')}
                  </label>
                  <input
                    type="text"
                    value={formData.mailUsername || ''}
                    onChange={(e) => handleInputChange('mailUsername', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('companies.mailUsernamePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.mailPassword')}
                  </label>
                  <input
                    type="password"
                    value={formData.mailPassword || ''}
                    onChange={(e) => handleInputChange('mailPassword', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('companies.mailPasswordPlaceholder')}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.mailSubject')}
                  </label>
                  <input
                    type="text"
                    value={formData.mailSubject || ''}
                    onChange={(e) => handleInputChange('mailSubject', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('companies.mailSubjectPlaceholder')}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companies.mailBody')}
                  </label>
                  <textarea
                    value={formData.mailBody || ''}
                    onChange={(e) => handleInputChange('mailBody', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('companies.mailBodyPlaceholder')}
                  />
                </div>
              </div>
            </div>
            */}
            {/* Form Actions */}
            <div id="company-submit-actions" className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/companies')}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t('companies.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{isEditMode ? t('companies.update') : t('companies.creating')}</span>
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    <span>{isEditMode ? t('companies.update') : t('companies.createCompany')}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Onboarding Tour Help Button */}
      {!isEditMode && (
        <button
          type="button"
          className="onboarding-help-btn"
          title={t('onboarding.replayTour')}
          onClick={() => {
            manualStart({
              tourId: 'company-create',
              steps: getCompanyTourSteps(),
            });
          }}
        >
          <HelpCircle size={22} />
        </button>
      )}
    </Layout>
  );

  function getCompanyTourSteps() {
    return [
      {
        element: '#company-basic-info',
        popover: {
          title: t('onboarding.company.basicInfoTitle'),
          description: t('onboarding.company.basicInfoDesc'),
          side: 'bottom' as const,
          align: 'start' as const,
        },
      },
      {
        element: '#company-legal-info',
        popover: {
          title: t('onboarding.company.legalInfoTitle'),
          description: t('onboarding.company.legalInfoDesc'),
          side: 'bottom' as const,
          align: 'start' as const,
        },
      },
      {
        element: '#company-cash-threshold',
        popover: {
          title: t('onboarding.company.cashThresholdTitle'),
          description: t('onboarding.company.cashThresholdDesc'),
          side: 'bottom' as const,
          align: 'start' as const,
        },
      },
      {
        element: '#company-address-info',
        popover: {
          title: t('onboarding.company.addressInfoTitle'),
          description: t('onboarding.company.addressInfoDesc'),
          side: 'bottom' as const,
          align: 'start' as const,
        },
      },
      {
        element: '#company-logo-upload',
        popover: {
          title: t('onboarding.company.logoTitle'),
          description: t('onboarding.company.logoDesc'),
          side: 'bottom' as const,
          align: 'start' as const,
        },
      },
      {
        element: '#company-invoice-settings',
        popover: {
          title: t('onboarding.company.invoiceSettingsTitle'),
          description: t('onboarding.company.invoiceSettingsDesc'),
          side: 'bottom' as const,
          align: 'start' as const,
        },
      },
      {
        element: '#company-submit-actions',
        popover: {
          title: t('onboarding.company.submitTitle'),
          description: t('onboarding.company.submitDesc'),
          side: 'top' as const,
          align: 'end' as const,
        },
      },
    ];
  }
};

export default CompanyCreatePage;
